import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useConfig } from '../../hooks/useConfig'
import { useVenues } from '../../hooks/useVenues'
import { updateConfig } from '../../services/api/updateConfig'
import { decodeConfigId } from '../../helpers/decodeConfigId'
import { buildConfigQuery } from '../../helpers/buildConfigQuery'
import { buildConfigDetailTitle } from '../../helpers/buildConfigDetailTitle'
import { EditEntityPage } from '../EditEntityPage/EditEntityPage'
import { ConfigTagsInput } from '../../components/ConfigTagsInput/ConfigTagsInput'
import { collectTags } from '../helpers/collectTags'
import { buildConfigEditRows } from './helpers/buildConfigEditRows'
import { buildRowsFromTags } from './helpers/buildRowsFromTags'
import type { TagRow } from '../../types'

export function ConfigEditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { getToken } = useAuth()

  const { venues, isLoading: venuesLoading } = useVenues()
  const venueOptions = useMemo(
    () => venues
      .map((v) => ({ value: v.id, label: `${v.venueSlug} (ID: ${v.id})` }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [venues],
  )

  const configFields = id ? decodeConfigId(id) : null
  const query = configFields ? buildConfigQuery(configFields) : ''
  const { configs, isLoading: configLoading, error } = useConfig(query)
  const isLoading = configLoading || venuesLoading

  const config = configFields
    ? configs.find((c) =>
        c.configKey === configFields.configKey &&
        c.tenantCode === configFields.tenantCode &&
        c.inventorySystemProfileCode === configFields.inventorySystemProfileCode &&
        c.venueId === configFields.venueId
      ) ?? null
    : null

  // Seed the tag editor from the config's current tags once it has loaded, and
  // reseed if the loaded config changes. This mirrors CreateConfigPage's
  // controlled tagRows state, but pre-filled with the existing tags. The seeding
  // runs in an effect (not during render) so it never triggers a setState in the
  // render phase; the seededConfigId guard keeps it to one reseed per config.
  const [tagRows, setTagRows] = useState<TagRow[]>([])
  const [seededConfigId, setSeededConfigId] = useState<string | null>(null)
  useEffect(() => {
    if (config && config.id !== seededConfigId) {
      setSeededConfigId(config.id)
      setTagRows(buildRowsFromTags(config.tags))
    }
  }, [config, seededConfigId])

  // The tags editor renders in the value cell of the "Tags" row, just before
  // Created (see buildConfigEditRows).
  const rows = config
    ? buildConfigEditRows(
        config,
        venueOptions,
        <ConfigTagsInput rows={tagRows} onChange={setTagRows} />,
      )
    : []

  async function handleSubmit(body: Record<string, unknown>) {
    await updateConfig(await getToken(), config!.id, {
      ...body,
      tags: collectTags(tagRows),
    })
    navigate(`/configs/${id}`)
  }

  return (
    <EditEntityPage
      title={config ? `Edit ${buildConfigDetailTitle(config)}` : 'Edit Config'}
      backTo={`/configs/${id}`}
      loadingLabel="config"
      rows={rows}
      isLoading={isLoading}
      loadError={error}
      onSubmit={handleSubmit}
    />
  )
}
